import { resolveScopeKey } from './scopeKeyData';
import { COMMITTEE_LOOKUP } from './committeeData';

// The local committee is a PROJECTION of the canonical scopeKey -- never a second table.
// scopeKey shape is LC<local>-<gca-segment>, e.g. LC1951-342A -> local committee 1951.
// Source of record: data/YCP_Location_Territory_Map.csv, generated into scopeKeyData.js.
// Returns '' when the location is not mapped; every caller already handles the empty
// case, and the claim still files and routes to the GC as UNROUTED (see resolveRouting).
export function resolveLocalCommittee(craft, railroad, workLocation) {
  const sk = resolveScopeKey(craft, railroad, workLocation);
  const m = /^LC(\d+)-/.exec(sk);
  return m ? m[1] : String();
}

// Routing resolution for a claim — the single source of the routing fallback policy.
//
// If the work location maps to a Local Chairperson, route there (routingStatus 'routed').
// If it does NOT map yet, capture the claim under a committee-level UNROUTED scopeKey so it
// still files: the deployed rules accept any non-empty scopeKey, and the GC has committee-wide
// read (inGCScope), so the claim lands in the GC's view for manual assignment. The member's
// claim and filing deadline are never lost to an unmapped location.
export function resolveRouting(craft, railroad, workLocation, committee) {
  const sk = resolveScopeKey(craft, railroad, workLocation);
  if (sk) return { scopeKey: sk, routingStatus: 'routed' };
  // A5 (audit 2026-09-22). This read `committee || 'GO-342'`, so a claim with NO committee was
  // captured under GO-342's unrouted key - it landed on one real committee's desk while belonging
  // to none. The whole point of an UNROUTED key is that it is visibly unplaced; prefixing it with
  // a real committee's name defeats that and makes it somebody's problem at random.
  //
  // 'UNKNOWN' is deliberately not a committee identifier: it matches no roster row, so it cannot be
  // mistaken for a routing and cannot be quietly worked by an officer who assumes it is his.
  return { scopeKey: (committee || 'UNKNOWN') + '|UNROUTED', routingStatus: 'unrouted' };
}

// The general committee is a PROJECTION of the scopeKey -- never an independent field.
// The GCA number lives inside the scopeKey's own suffix, so the two cannot legitimately
// disagree, and where they do, the scopeKey is the authority (SS XI).
//
//   LC1951-342A      -> GO-342
//   GO-342|UNROUTED  -> GO-342
//
// Returns '' when the key cannot be parsed. Callers must leave `committee` untouched in that
// case rather than writing a guess: an empty committee is invisible to the committee-scoped
// queries in Claims.js and Dashboard.js, and a WRONG one is worse than absent.
export function resolveGeneralCommittee(scopeKey) {
  const s = String(scopeKey || '');
  // A12: this required DIGITS, so GO-SCL, GO-GTW and GO-DMIR returned '' - and the unrouted
  // branch is the only branch they can reach, because all 71 SCOPE_KEY_LOOKUP entries are 342.
  // 'UNKNOWN|UNROUTED' still does not match, which is the point of the paragraph above.
  const unrouted = /^(GO-[A-Z0-9]+)\|UNROUTED$/.exec(s);
  if (unrouted) return unrouted[1];
  const routed = /^LC\d+-(\d{3})[A-Z]?$/.exec(s);
  return routed ? 'GO-' + routed[1] : String();
}

// THE APPLICANT'S ROUTE - the same three keys a claim routes on (2026-09-22, task list section 341).
//
// Bob's doctrine of record, data/LC_REPRESENTATION_DOCTRINE.md section 5: "LC resolves on three keys:
// craft x railroad x territory", and section 5.2: the local number does NOT identify an LC. So an
// applicant is never asked to TYPE his General Committee or his Local - both are projections, exactly
// as they are for a claim. Section 9: "Do not deny or delay any claim because LC assignment is
// uncertain. When in doubt, escalate to the GC of the relevant committee." So an unmapped location
// still resolves a committee (from craft and railroad) and an EMPTY local, and the application goes
// to that committee's General Chairperson, visibly unrouted.
//
// Pure. The form shows the result before he submits; the Cloud Function computes it again on arrival
// and uses only its own answer.
export function resolveApplicantRouting(craft, railroad, workLocation) {
  const sk = resolveScopeKey(craft, railroad, workLocation);
  const fromTable = (COMMITTEE_LOOKUP[craft] && COMMITTEE_LOOKUP[craft][railroad]) || String();
  if (sk) {
    const m = /^LC(\d+)-/.exec(sk);
    return {
      scopeKey: sk,
      localCommittee: m ? m[1] : String(),
      // The scopeKey's own GCA segment is the authority where it parses (resolveGeneralCommittee).
      generalCommittee: resolveGeneralCommittee(sk) || fromTable,
      routingStatus: 'routed',
    };
  }
  return { scopeKey: String(), localCommittee: String(), generalCommittee: fromTable, routingStatus: 'unrouted' };
}
